import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";
import type { paths as CmmdForgeContextPaths } from "./cmmd-generated";

/**
 * Shared CMMD data contracts consumed by both the Forge server and web app.
 *
 * Keep this file to schema/type definitions plus pure vocabulary helpers — no
 * side-effecting or IO logic. Types here define the canonical shape of data
 * flowing between CMMD and Forge.
 */

// ── Identity ──────────────────────────────────────────────────────────────────

const CmmdAvatarUrl = TrimmedNonEmptyString.check(Schema.isMaxLength(256 * 1024));

export const CmmdUserIdentitySchema = Schema.Struct({
  id: TrimmedNonEmptyString,
  firstName: Schema.NullOr(Schema.String),
  lastName: Schema.NullOr(Schema.String),
  email: TrimmedNonEmptyString,
  username: TrimmedNonEmptyString,
  organizationId: Schema.optionalKey(TrimmedNonEmptyString),
  avatarUrl: Schema.optionalKey(Schema.NullOr(CmmdAvatarUrl)),
  planLabel: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
});
export type CmmdUserIdentity = typeof CmmdUserIdentitySchema.Type;

/**
 * Versioned alias for the canonical Forge↔CMMD auth identity contract (v1).
 *
 * `ForgeAuthIdentityV1` is the stable type name consumers should reference when
 * they need to document or enforce the Forge auth identity shape across package
 * boundaries. It is structurally identical to {@link CmmdUserIdentity} — a type
 * alias rather than a separate schema — so no runtime coercion is needed:
 * `normalizeCmmdUserIdentity` already produces valid `ForgeAuthIdentityV1`
 * values, and `CmmdUserIdentitySchema` validates them at the boundary.
 *
 * If the shape ever needs to diverge (e.g. a `schemaVersion` discriminant or
 * new required fields), introduce `ForgeAuthIdentityV2` as a new schema rather
 * than mutating this one.
 */
export type ForgeAuthIdentityV1 = CmmdUserIdentity;

/**
 * Schema counterpart of {@link ForgeAuthIdentityV1}. Use for decoding /
 * validating identity payloads at Forge↔CMMD API boundaries.
 */
export const ForgeAuthIdentityV1Schema = CmmdUserIdentitySchema;

export interface CmmdProfileData {
  readonly identity: CmmdUserIdentity;
  readonly source: "local-session";
  readonly warnings: ReadonlyArray<string>;
}

/**
 * Window a Forge usage page asks CMMD for. Days are inclusive local dates in
 * `timeZone`; CMMD buckets its `ai_request_log` rows with `AT TIME ZONE` so a
 * late-night request lands on the day the user experienced.
 */
export interface CmmdUsageSummaryInput {
  readonly sinceDay: string;
  readonly untilDay: string;
  readonly timeZone: string;
  /**
   * Omit for days. On `"hour"` CMMD cuts the same rows into whole hours
   * measured from `sinceTime`, the way each environment's transcript scanner
   * cuts its own — without this it can only answer in days, and a day-only
   * bucket is invisible to the client's hourly chart.
   */
  readonly resolution?: "day" | "hour";
  /** Inclusive UTC instant an hourly window starts at, and its bucket anchor. */
  readonly sinceTime?: string;
  /** Exclusive UTC instant an hourly window ends at. */
  readonly untilTime?: string;
}

// ── Workspace / Org ───────────────────────────────────────────────────────────

export interface CmmdWorkspace {
  readonly id: string;
  readonly name: string;
  readonly slug: string | null;
  readonly color: string | null;
  readonly logoUrl: string | null;
  readonly iconUrl: string | null;
}

// ── Campaigns ─────────────────────────────────────────────────────────────────

export interface CmmdCampaign {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly workspaceId?: string | null;
  readonly description: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
}

// ── Projects ──────────────────────────────────────────────────────────────────

export interface CmmdProject {
  readonly id: string;
  /**
   * The board name of record. This is what the CMMD Projects board, reports, and
   * agent write-backs use, and the only name a join or search may key on.
   */
  readonly title: string;
  /**
   * Optional display name for Forge's own chrome — sidebar, breadcrumb, thread
   * lists. Null means "render `title`". Resolve it through
   * `projectDisplayName()` rather than reading it directly, so the fallback is
   * applied in one place.
   */
  readonly shortName?: string | null;
  /** Stable CMMD-owned namespace used to render work-item keys such as FOR-1412. */
  readonly workItemPrefix?: string | null;
  readonly status: string | null;
  readonly priority?: string | null;
  readonly campaignId: string | null;
  /** Human-readable campaign name carried by the project; allows campaign
   *  existence + title to be derived from projects without a separate campaigns
   *  call (used by the CMMD Center N+1 fix). */
  readonly campaignTitle?: string | null;
  readonly workspaceId?: string | null;
  readonly workspaceName?: string | null;
  /** Resolved workspace (organization) logo URL — rendered in the Center's Workspace chip. */
  readonly workspaceIconUrl?: string | null;
  readonly icon?: string | null;
  readonly iconUrl?: string | null;
  readonly ownerId?: string | null;
  readonly ownerName?: string | null;
  readonly ownerAvatarUrl?: string | null;
  readonly teamId?: string | null;
  readonly teamName?: string | null;
  readonly startDate?: string | null;
  readonly targetEndDate?: string | null;
  readonly createdAt?: string | null;
  readonly updatedAt?: string | null;
  readonly githubRepoUrl?: string | null;
  readonly projectLinks?: ReadonlyArray<{
    readonly title?: string | null;
    readonly url?: string | null;
    readonly type?: string | null;
  }> | null;
  readonly description: string | null;
  readonly brief: string | null;
}

// ── Project move ──────────────────────────────────────────────────────────────

export interface CmmdMoveReportCount {
  readonly table: string;
  // null for cascade-only tables that have no org column of their own.
  readonly orgColumn: string | null;
  readonly classification: string;
  readonly rows: number;
}

export interface CmmdOwnerResolution {
  readonly ownerId: string | number | null;
  readonly reason: string;
}

export interface CmmdCampaignResolution {
  // The campaign the project ends up on after the move (null = detached / no campaign).
  readonly campaignId: string | number | null;
  readonly reason: "no-campaign" | "kept-same-org" | "detached" | "reassigned";
}

export interface CmmdMoveReport {
  readonly applied: boolean;
  readonly sourceOrgId: string | number;
  readonly targetOrgId: string | number;
  readonly ownerResolution: CmmdOwnerResolution;
  // Always present — the backend resolves a campaign outcome for every move.
  readonly campaignResolution: CmmdCampaignResolution;
  readonly counts: ReadonlyArray<CmmdMoveReportCount>;
  readonly total: number;
}

// ── Priority (canonical vocabulary) ────────────────────────────────────────────

/**
 * The canonical priority vocabulary shared with the CMMD backend — the single
 * source of truth for Forge's project/work-item priority levels. Ordered
 * highest → lowest. Legacy `urgent` is reconciled to `critical`.
 *
 * Note: wire fields like {@link CmmdProject.priority} intentionally stay
 * `string | null` (tolerant reader) — existing data is free-text. Use
 * {@link normalizeCmmdPriority} to resolve a raw value to one of these levels.
 */
export type CmmdPriority = "critical" | "high" | "medium" | "low" | "lowest";

/** Canonical priority levels, highest → lowest. Drives dropdown order + sorting. */
export const CMMD_PRIORITY_ORDER: readonly CmmdPriority[] = [
  "critical",
  "high",
  "medium",
  "low",
  "lowest",
];

const CMMD_PRIORITY_SET = new Set<string>(CMMD_PRIORITY_ORDER);

/**
 * Resolve a raw, free-text priority to a canonical {@link CmmdPriority}.
 *
 * Lowercases + trims, maps the legacy `urgent` level to `critical`, and returns
 * `null` for anything outside the canonical vocabulary (empty, nullish, or
 * unknown) so callers can render/sort nothing for unrecognized values.
 */
export function normalizeCmmdPriority(raw: string | null | undefined): CmmdPriority | null {
  if (raw === null || raw === undefined) return null;
  const normalized = raw.trim().toLowerCase();
  if (normalized === "urgent") return "critical";
  return CMMD_PRIORITY_SET.has(normalized) ? (normalized as CmmdPriority) : null;
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

export interface CmmdTask {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly priority: string | null;
  readonly projectId: string | null;
  readonly campaignId: string | null;
  readonly workspaceId?: string | null;
  readonly workspaceName?: string | null;
  readonly teamId?: string | null;
  readonly teamName?: string | null;
  readonly dueDate: string | null;
  readonly updatedAt: string | null;
  readonly description: string | null;
  readonly assigneeId: string | null;
  /**
   * Task ids this task is blocked by. Empty array means 'no blockers, ready'.
   * Hydrated from the CMMD MCP `list_board_tasks` response. Older CMMD
   * backends that don't yet emit the field are treated as empty — tasks
   * surface as always-ready.
   */
  readonly dependsOn: ReadonlyArray<string>;
  /** Populated only on full task detail fetch (GET /api/tasks/:id). Absent on list/intake responses. */
  readonly comments?: ReadonlyArray<CmmdComment>;
  /** Populated only on full task detail fetch. Absent on list/intake responses. */
  readonly attachments?: ReadonlyArray<CmmdAttachment>;
  /** ISO timestamp when the task was archived. Null if active. */
  readonly archivedAt?: string | null;
}

/** Atomic approved-worker-plan aliases generated from CMMD's forge-context OpenAPI. */
type CmmdApprovedWorkerPlanMaterializationOperation = NonNullable<
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/approved-worker-plans/{promptId}/materialize"]["post"]
>;
export type CmmdMaterializeApprovedWorkerPlanParams = NonNullable<
  CmmdApprovedWorkerPlanMaterializationOperation["requestBody"]
>["content"]["application/json"];
export type CmmdApprovedWorkerPlanItem = CmmdMaterializeApprovedWorkerPlanParams["items"][number];
export type CmmdMaterializedApprovedWorkerPlan =
  CmmdApprovedWorkerPlanMaterializationOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdApprovedWorkerPlanTask = CmmdMaterializedApprovedWorkerPlan["tasks"][number];

// ── Brain docs ────────────────────────────────────────────────────────────────

export interface CmmdBrainDoc {
  readonly id: string;
  readonly title: string;
  readonly content: string | null;
  readonly tags: ReadonlyArray<string>;
  readonly updatedAt: string | null;
}

// ── Comments ──────────────────────────────────────────────────────────────────

type CmmdTaskCommentsOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/comments"];

export type CmmdTaskComment =
  CmmdTaskCommentsOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CreateCmmdTaskCommentParams = NonNullable<
  CmmdTaskCommentsOperation["post"]["requestBody"]
>["content"]["application/json"];
type CmmdTaskCommentMutationOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/comments/{commentKey}"];
export type UpdateCmmdTaskCommentParams = NonNullable<
  CmmdTaskCommentMutationOperation["patch"]["requestBody"]
>["content"]["application/json"];
export type DeleteCmmdTaskCommentParams = NonNullable<
  CmmdTaskCommentMutationOperation["delete"]["requestBody"]
>["content"]["application/json"];
export type CmmdTaskCommentDeleteReceipt =
  CmmdTaskCommentMutationOperation["delete"]["responses"][200]["content"]["application/json"]["data"];

export interface CmmdComment {
  readonly id: string;
  readonly commentKey?: string;
  readonly content: string;
  readonly authorId: string;
  readonly authorName: string | null;
  readonly entityId: string;
  readonly entityType: "task" | "project";
  readonly createdAt: string;
  readonly updatedAt: string | null;
  readonly permissions?: {
    readonly canEdit: boolean;
    readonly canDelete: boolean;
  };
  readonly user?: {
    readonly firstName: string;
    readonly lastName: string | null;
    readonly profileImage: string | null;
  };
  readonly actor?:
    | {
        readonly kind: "human_user";
        readonly displayName: string;
        readonly profileImage: string | null;
      }
    | { readonly kind: "agent_member"; readonly displayName: string };
}

// ── Members / Users ───────────────────────────────────────────────────────────

export interface CmmdMember {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly role: string | null;
  readonly avatarUrl: string | null;
}

type AddProjectTeamMemberOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/team"]["post"];
export type AddCmmdProjectMemberParams = NonNullable<
  AddProjectTeamMemberOperation["requestBody"]
>["content"]["application/json"];
export type AddCmmdProjectMemberResult =
  AddProjectTeamMemberOperation["responses"][201]["content"]["application/json"]["data"];

/** Project health status for a status update. */
export type CmmdProjectUpdateStatus = "on_track" | "at_risk" | "off_track" | "complete";

/**
 * A Linear-style project status update as the Forge UI reads it — the author's
 * display name + avatar resolved, distinct from the raw DB-shaped
 * {@link CmmdProjectUpdate} (which only carries authorId).
 */
export interface CmmdProjectUpdateView {
  readonly id: string;
  readonly status: CmmdProjectUpdateStatus;
  readonly body: string;
  readonly authorName: string | null;
  readonly authorAvatarUrl: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly canEdit: boolean;
}

/** Standard page metadata returned by canonical forge-context collection routes. */
export interface CmmdPaginationMeta {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/** One normalized page of project updates for Forge project surfaces. */
export interface CmmdProjectUpdatesPage {
  readonly updates: ReadonlyArray<CmmdProjectUpdateView>;
  readonly pagination: CmmdPaginationMeta;
}

/** Canonical project Markdown surfaces supported by CMMD readability formatting. */
export type CmmdProjectContentSurface = "description" | "status_update" | "task_description";

/** Draft-only result from CMMD's workspace-metered readability formatter. */
export interface CmmdProjectContentReadabilityResult {
  readonly content: string;
  readonly provider: string;
  readonly model: string;
  readonly billingSource: "workspace";
}

// ── Attachments ───────────────────────────────────────────────────────────────

type CmmdTaskAttachmentsOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/attachments"];

/**
 * Derived from the generated operation, not hand-typed.
 *
 * This interface was previously written out by hand and declared
 * `url: string` while the surface actually returns `string | null` — CMMD signs
 * per request and yields null when one object fails to sign, so a single bad key
 * does not fail the whole list. Nothing could detect that drift: regeneration
 * only rewrites the generated file, leaving a stale hand-written twin
 * compiling happily.
 *
 * `url` remains nullable here for that reason; consumers must handle it. Read it
 * from the response that produced it and never persist it, because it expires
 * within the hour.
 */
export type CmmdAttachment =
  CmmdTaskAttachmentsOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];

// ── Thread attachments (cloud persistence for composer files) ────────────────

type CmmdThreadAttachmentsOperation =
  CmmdForgeContextPaths["/api/forge/context/thread-attachments/{threadId}"];
type CmmdThreadAttachmentDownloadOperation =
  CmmdForgeContextPaths["/api/forge/context/thread-attachments/{threadId}/{attachmentId}/download"];

/**
 * One cloud-persisted thread attachment, derived from the generated operation
 * (same rule as `CmmdAttachment` above: hand-typed twins drift silently).
 * `url` is signed for the response that carried it and expires within minutes;
 * read it once, never persist it.
 */
export type CmmdThreadAttachment =
  CmmdThreadAttachmentsOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];

/** Fresh signed download URL for one thread attachment, with its own expiry. */
export type CmmdThreadAttachmentDownload =
  CmmdThreadAttachmentDownloadOperation["get"]["responses"][200]["content"]["application/json"]["data"];

// ── Work intake (snapshot fetched from CMMD into Forge) ───────────────────────

export interface CmmdWorkIntake {
  readonly connected: boolean;
  readonly source: string;
  readonly fetchedAt: string;
  readonly workspace: CmmdWorkspace | null;
  readonly workspaces: ReadonlyArray<CmmdWorkspace>;
  readonly identity: CmmdUserIdentity | null;
  readonly campaigns: ReadonlyArray<CmmdCampaign>;
  readonly projects: ReadonlyArray<CmmdProject>;
  readonly tasks: ReadonlyArray<CmmdTask>;
  readonly warnings: ReadonlyArray<string>;
}

// ── MCP resource payloads ─────────────────────────────────────────────────────

/** Shape of a CMMD MCP JSON-RPC response wrapper */
export interface CmmdMcpResourceResult {
  readonly contents: ReadonlyArray<{
    readonly uri: string;
    readonly mimeType?: string;
    readonly text?: string;
  }>;
}

export interface CmmdMcpToolResult {
  readonly content: ReadonlyArray<{
    readonly type: "text" | "image" | "resource";
    readonly text?: string;
    readonly mimeType?: string;
    readonly data?: string;
    readonly resource?: {
      readonly uri: string;
      readonly text?: string;
    };
  }>;
  readonly isError?: boolean;
}

// ── Project repository state ──────────────────────────────────────────────────

/** Whether a CMMD project has a repository and a linked Forge execution. */
export type CmmdProjectRepoState = "no_repo" | "repo_linked" | "repo_linked_forge_linked";

/** Forge-side link record returned by GET /api/forge/context/projects/:id/repository */
export interface CmmdProjectRepository {
  readonly projectId: number;
  readonly repoState: CmmdProjectRepoState;
  readonly forgeLink: {
    readonly forgeEnvironmentId: string;
    readonly forgeProjectId: string;
    readonly forgeWorkspaceRoot: string | null;
    readonly githubRepoFullName: string | null;
  } | null;
}

export const CmmdProjectContextLinkSchema = Schema.Struct({
  id: Schema.Number,
  organizationId: Schema.Number,
  projectId: Schema.Number,
  forgeEnvironmentId: TrimmedNonEmptyString,
  forgeProjectId: TrimmedNonEmptyString,
  forgeWorkspaceRoot: Schema.NullOr(Schema.String),
  githubRepoFullName: Schema.NullOr(Schema.String),
  source: Schema.Union([
    Schema.Literal("linked_existing"),
    Schema.Literal("created_from_cmmd"),
    Schema.Literal("created_from_forge"),
  ]),
  linkedByUserId: Schema.NullOr(Schema.Union([Schema.Number, Schema.String])),
  linkedAt: TrimmedNonEmptyString,
  lastVerifiedAt: Schema.NullOr(Schema.String),
  projectTitle: TrimmedNonEmptyString,
  projectCampaignId: Schema.NullOr(Schema.Number),
});
export type CmmdProjectContextLink = typeof CmmdProjectContextLinkSchema.Type;

export const CmmdProjectContextLinksResponseSchema = Schema.Struct({
  links: Schema.Array(CmmdProjectContextLinkSchema),
});

export const CmmdEnsuredGitHubProjectContextSchema = Schema.Struct({
  project: Schema.Struct({
    id: Schema.Number,
    title: TrimmedNonEmptyString,
    campaignId: Schema.NullOr(Schema.Number),
  }),
  source: Schema.Union([Schema.Literal("linked_existing"), Schema.Literal("created_from_cmmd")]),
  created: Schema.Boolean,
});
export type CmmdEnsuredGitHubProjectContext = typeof CmmdEnsuredGitHubProjectContextSchema.Type;
export type CmmdProjectContextLinksResponse = typeof CmmdProjectContextLinksResponseSchema.Type;

export const CmmdEnsuredProjectRepositorySchema = Schema.Struct({
  project: Schema.Struct({
    id: Schema.Number,
    title: TrimmedNonEmptyString,
    campaignId: Schema.NullOr(Schema.Number),
  }),
  repository: Schema.Struct({
    githubRepoFullName: TrimmedNonEmptyString,
    githubRepoUrl: TrimmedNonEmptyString,
  }),
  created: Schema.Boolean,
});
export type CmmdEnsuredProjectRepository = typeof CmmdEnsuredProjectRepositorySchema.Type;

type CmmdProjectRepositoriesOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/repositories"]["get"];

/** Canonical many-to-many CMMD project repository link generated from forge-context OpenAPI. */
export type CmmdProjectRepositoryLink =
  CmmdProjectRepositoriesOperation["responses"][200]["content"]["application/json"]["data"]["repositories"][number];

export type CmmdProjectRepositories = ReadonlyArray<CmmdProjectRepositoryLink>;

// ── Project board ─────────────────────────────────────────────────────────────

type CmmdProjectBoardOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board"];
type CmmdProjectBoardUpdateOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board/{taskId}"]["patch"];
type CmmdProjectBoardStatusOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board/{taskId}/status"]["patch"];
type CmmdProjectBoardMoveOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board/{taskId}/move"]["patch"];
type CmmdProjectAgentMembersOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members"];
type CmmdProjectAgentMemberOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/{memberId}"];
type CmmdProjectAgentMemberRetireOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/{memberId}/retire"]["post"];
type CmmdProjectAgentMemberInstancesOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/{memberId}/instances"]["get"];
type CmmdProjectAgentMemberAvatarOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/{memberId}/avatar"]["put"];
type CmmdProjectAgentMemberConsolidationPreviewOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/consolidation/preview"]["post"];
type CmmdProjectAgentMemberConsolidationApplyOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/agent-members/consolidation/apply"]["post"];
type CmmdProjectTaskAssignmentOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/tasks/{taskId}/assignment"]["put"];
type CmmdTaskExecutionOptionsOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board/{taskId}/execution-options"]["get"];
type CmmdCreateWorkSessionOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/board/{taskId}/work-sessions"]["post"];
type CmmdListWorkSessionsOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/work-sessions"]["get"];
type CmmdWorkSessionOperation =
  CmmdForgeContextPaths["/api/forge/context/work-sessions/{workSessionId}"];
type CmmdAttachWorkSessionThreadOperation =
  CmmdForgeContextPaths["/api/forge/context/work-sessions/{workSessionId}/threads"]["post"];
type CmmdTaskConversationsOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/conversations"]["get"];
type CmmdLinkTaskConversationOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/conversations"]["post"];
type CmmdTaskConversationOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/conversations/{conversationId}"]["get"];
type CmmdTaskHistoryOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/history"]["get"];
type CmmdUpsertTaskPullRequestOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}/pull-requests"]["put"];
type CmmdCenterBoardOperation = CmmdForgeContextPaths["/api/forge/context/work-items/board"];
type CmmdCenterBoardMoveOperation =
  CmmdForgeContextPaths["/api/forge/context/work-items/board/{taskId}/move"]["patch"];
type CmmdProjectTaskWorkflowOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-workflow"];

type CmmdProjectTaskDraftCreateOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-drafts"]["post"];
type CmmdProjectTaskDraftOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-drafts/{draftId}"];
type CmmdProjectTaskDraftPreviewOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-drafts/{draftId}/preview"]["post"];
type CmmdProjectTaskDraftCommitOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-drafts/{draftId}/commit"]["post"];
type CmmdProjectTaskDraftDiscardOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/task-drafts/{draftId}/discard"]["post"];

/** User-owned, review-before-commit Project Lead task-tree draft. */
export type CmmdProjectTaskDraft =
  CmmdProjectTaskDraftCreateOperation["responses"][201]["content"]["application/json"]["data"];
export type CreateCmmdProjectTaskDraftParams = NonNullable<
  CmmdProjectTaskDraftCreateOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectTaskDraftProposal = NonNullable<
  CreateCmmdProjectTaskDraftParams["proposal"]
>;
export type CmmdProjectTaskDraftTask = CmmdProjectTaskDraftProposal["tasks"][number];
export type CmmdProjectTaskDraftSuggestion = CmmdProjectTaskDraft["suggestions"][number];
export type UpdateCmmdProjectTaskDraftParams = NonNullable<
  NonNullable<CmmdProjectTaskDraftOperation["patch"]>["requestBody"]
>["content"]["application/json"];
export type CmmdProjectTaskDraftMutation = UpdateCmmdProjectTaskDraftParams["mutation"];
export type PreviewCmmdProjectTaskDraftParams = NonNullable<
  CmmdProjectTaskDraftPreviewOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectTaskDraftPreview =
  CmmdProjectTaskDraftPreviewOperation["responses"][200]["content"]["application/json"]["data"];
export type CommitCmmdProjectTaskDraftParams = NonNullable<
  CmmdProjectTaskDraftCommitOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectTaskDraftCommit =
  CmmdProjectTaskDraftCommitOperation["responses"][200]["content"]["application/json"]["data"];
export type DiscardCmmdProjectTaskDraftParams = NonNullable<
  CmmdProjectTaskDraftDiscardOperation["requestBody"]
>["content"]["application/json"];

/** Canonical project-board task generated from the CMMD Forge Context OpenAPI. */
export type CmmdBoardTask =
  CmmdProjectBoardOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdTaskStatus = NonNullable<
  NonNullable<CmmdProjectBoardStatusOperation["requestBody"]>["content"]["application/json"]
>["status"];
export type CmmdWorkItemType = CmmdBoardTask["workItemType"];
export type CmmdProjectAgentMember =
  CmmdProjectAgentMembersOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CreateCmmdProjectAgentMemberParams = NonNullable<
  CmmdProjectAgentMembersOperation["post"]["requestBody"]
>["content"]["application/json"];
export type CreateCmmdProjectAgentMemberResult =
  CmmdProjectAgentMembersOperation["post"]["responses"][201]["content"]["application/json"]["data"];
export type RenameCmmdProjectAgentMemberParams = NonNullable<
  CmmdProjectAgentMemberOperation["patch"]["requestBody"]
>["content"]["application/json"];
export type RenameCmmdProjectAgentMemberResult =
  CmmdProjectAgentMemberOperation["patch"]["responses"][200]["content"]["application/json"]["data"];
export type RetireCmmdProjectAgentMemberParams = NonNullable<
  CmmdProjectAgentMemberRetireOperation["requestBody"]
>["content"]["application/json"];
export type RetireCmmdProjectAgentMemberResult =
  CmmdProjectAgentMemberRetireOperation["responses"][201]["content"]["application/json"]["data"];
export type UpdateCmmdProjectAgentMemberAvatarParams = NonNullable<
  CmmdProjectAgentMemberAvatarOperation["requestBody"]
>["content"]["application/json"];
export type UpdateCmmdProjectAgentMemberAvatarResult =
  CmmdProjectAgentMemberAvatarOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectAgentInstance =
  CmmdProjectAgentMemberInstancesOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdProjectAgentMemberConsolidationPreview =
  CmmdProjectAgentMemberConsolidationPreviewOperation["responses"][200]["content"]["application/json"]["data"];
export type ApplyCmmdProjectAgentMemberConsolidationParams = NonNullable<
  CmmdProjectAgentMemberConsolidationApplyOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectAgentMemberConsolidationResult =
  CmmdProjectAgentMemberConsolidationApplyOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectTaskAssignmentParams = NonNullable<
  CmmdProjectTaskAssignmentOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectTaskAssignmentSubject = CmmdProjectTaskAssignmentParams["subject"];
export type CmmdProjectTaskAssignmentResult =
  CmmdProjectTaskAssignmentOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdTaskWorkflow =
  CmmdProjectTaskWorkflowOperation["get"]["responses"][200]["content"]["application/json"]["data"];
export type CreateCmmdBoardTaskParams = NonNullable<
  CmmdProjectBoardOperation["post"]["requestBody"]
>["content"]["application/json"];
export type CmmdBoardTaskCreateStatus = NonNullable<CreateCmmdBoardTaskParams["status"]>;
export type UpdateCmmdBoardTaskParams = NonNullable<
  CmmdProjectBoardUpdateOperation["requestBody"]
>["content"]["application/json"];
export type CreateCmmdCenterBoardTaskParams = NonNullable<
  CmmdCenterBoardOperation["post"]["requestBody"]
>["content"]["application/json"];

export type CmmdFeedbackType = "bug" | "feature";

/** Body for POST /api/cmmd/feedback */
export interface CreateCmmdFeedbackParams {
  readonly type: CmmdFeedbackType;
  readonly title: string;
  readonly description?: string;
  readonly priority?: "low" | "medium" | "high" | "urgent";
  readonly source: "forge";
  readonly senderName?: string;
}

/** Result from CMMD's feedback intake router. */
export interface CmmdFeedbackIntakeResult {
  readonly id: number | string;
  readonly title: string;
  readonly type: CmmdFeedbackType;
  readonly projectId?: number | string | null;
  readonly projectTitle?: string | null;
  readonly campaignId?: number | string | null;
  readonly campaignTitle?: string | null;
  readonly workspaceId?: number | string | null;
  readonly workspaceName?: string | null;
}

export type ListCmmdCenterBoardParams = NonNullable<
  CmmdCenterBoardOperation["get"]["parameters"]["query"]
>;
export type CmmdCenterBoardResponse =
  CmmdCenterBoardOperation["get"]["responses"][200]["content"]["application/json"]["data"];
export type MoveCmmdCenterBoardTaskParams = NonNullable<
  CmmdCenterBoardMoveOperation["requestBody"]
>["content"]["application/json"];
export type MoveCmmdProjectBoardTaskParams = NonNullable<
  CmmdProjectBoardMoveOperation["requestBody"]
>["content"]["application/json"];
export type CmmdTaskExecutionOption =
  CmmdTaskExecutionOptionsOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdWorkSession =
  CmmdListWorkSessionsOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdCreateWorkSessionParams = NonNullable<
  CmmdCreateWorkSessionOperation["requestBody"]
>["content"]["application/json"];
export type CmmdCreateWorkSessionResult =
  CmmdCreateWorkSessionOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdListWorkSessionsParams = NonNullable<
  CmmdListWorkSessionsOperation["parameters"]["query"]
>;
export type CmmdWorkSessionStatus = NonNullable<
  NonNullable<CmmdWorkSessionOperation["patch"]["requestBody"]>["content"]["application/json"]
>["status"];
export type CmmdAttachWorkSessionThreadParams = NonNullable<
  CmmdAttachWorkSessionThreadOperation["requestBody"]
>["content"]["application/json"];
export type CmmdAttachWorkSessionThreadResult =
  CmmdAttachWorkSessionThreadOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdTaskConversationList =
  CmmdTaskConversationsOperation["responses"][200]["content"]["application/json"]["data"];
export type LinkCmmdTaskConversationParams = NonNullable<
  CmmdLinkTaskConversationOperation["requestBody"]
>["content"]["application/json"];
export type LinkCmmdTaskConversationResult =
  CmmdLinkTaskConversationOperation["responses"][201]["content"]["application/json"]["data"];
type GeneratedCmmdTaskConversationSummary = CmmdTaskConversationList["items"][number];
export type CmmdTaskConversationSummary = Omit<
  GeneratedCmmdTaskConversationSummary,
  "participants"
> & {
  readonly participants?: readonly GeneratedCmmdTaskConversationSummary["participants"][number][];
};
type GeneratedCmmdTaskConversation =
  CmmdTaskConversationOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdTaskConversation = Omit<GeneratedCmmdTaskConversation, "summary"> & {
  readonly summary: CmmdTaskConversationSummary;
};
export type CmmdTaskHistory =
  CmmdTaskHistoryOperation["responses"][200]["content"]["application/json"]["data"];
export type UpsertCmmdTaskPullRequestParams = NonNullable<
  CmmdUpsertTaskPullRequestOperation["requestBody"]
>["content"]["application/json"];
export type UpsertCmmdTaskPullRequestResult =
  CmmdUpsertTaskPullRequestOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdTaskConversationListParams = NonNullable<
  CmmdTaskConversationsOperation["parameters"]["query"]
>;
export type CmmdTaskConversationParams = NonNullable<
  CmmdTaskConversationOperation["parameters"]["query"]
>;

type CmmdForgeAutomationPresetsOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/presets"]["get"];
type CmmdForgeAutomationRulesOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/projects/{projectId}/rules"]["get"];
type CmmdUserForgeAutomationRulesOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/user/rules"];
type CmmdUserForgeAutomationRuleOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/user/rules/{ruleId}"]["patch"];
type CmmdUserForgeAutomationRunsOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/user/rules/{ruleId}/runs"];
type CmmdCreateForgeAutomationRuleOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/projects/{projectId}/rules"]["post"];
type CmmdUpdateForgeAutomationRuleOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/projects/{projectId}/rules/{ruleId}"]["patch"];
type CmmdForgeAutomationRunsOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/projects/{projectId}/runs"]["get"];
type CmmdStartForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/projects/{projectId}/tasks/{taskId}/runs"]["post"];
type CmmdForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}"]["get"];
type CmmdRetryForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}/retry"]["post"];
type CmmdCancelForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}/cancel"]["post"];
type CmmdApproveForgeAutomationEscalationOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}/escalations/approve"]["post"];
type CmmdRejectForgeAutomationEscalationOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}/escalations/reject"]["post"];
type CmmdClaimForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runs/{runId}/claim"]["post"];
type CmmdRuntimeForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}"]["get"];
type CmmdRuntimeTerminalSnapshotOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-authority/runtimes/{runtimeId}/automations/runs/{runId}/terminal-snapshot"]["get"];
type CmmdHeartbeatForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}/heartbeat"]["post"];
type CmmdReleaseForgeAutomationRunOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}/release"]["post"];
type CmmdReportForgeAutomationStageOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}/stage-reports"]["post"];
type CmmdResolveForgeAutomationModelPolicyOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}/model-resolution"]["post"];
type CmmdCreateForgeAutomationChildWorkItemOperation =
  CmmdForgeContextPaths["/api/forge/context/automations/runtime/runs/{runId}/child-work-items"]["post"];
type CmmdForgeAccountPreferencesGetOperation =
  CmmdForgeContextPaths["/api/forge/context/account/preferences"]["get"];
type CmmdForgeAccountPreferencesPatchOperation =
  CmmdForgeContextPaths["/api/forge/context/account/preferences"]["patch"];
type CmmdOrgProviderPolicyGetOperation =
  CmmdForgeContextPaths["/api/forge/context/org/provider-policy"]["get"];
type CmmdCodexAccountIdentityPostOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/codex/account-identity"]["post"];
type CmmdCodexCredentialGetOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/codex/credential"]["get"];
type CmmdClaudeCredentialGetOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/claude/credential"]["get"];
type CmmdClaudeRuntimeReadinessGetOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/claude/runtime-readiness/{runtimeId}"]["get"];
type CmmdClaudeRuntimeReadTicketOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/claude/runtime-read-ticket"]["post"];
export type CreateCmmdClaudeRuntimeReadTicketParams = Readonly<
  NonNullable<CmmdClaudeRuntimeReadTicketOperation["requestBody"]>["content"]["application/json"]
>;
type CmmdProviderUsageGetOperation =
  CmmdForgeContextPaths["/api/forge/context/providers/usage"]["get"];
type CmmdSavedDataViewsGetOperation =
  CmmdForgeContextPaths["/api/forge/context/saved-views"]["get"];
type CmmdSavedDataViewsPostOperation =
  CmmdForgeContextPaths["/api/forge/context/saved-views"]["post"];
type CmmdSavedDataViewsImportOperation =
  CmmdForgeContextPaths["/api/forge/context/saved-views/import"]["post"];
type CmmdSavedDataViewsPatchOperation =
  CmmdForgeContextPaths["/api/forge/context/saved-views/{viewId}"]["patch"];
type CmmdSavedDataViewsDeleteOperation =
  CmmdForgeContextPaths["/api/forge/context/saved-views/{viewId}"]["delete"];
type CmmdUserSettingsGetOperation =
  CmmdForgeContextPaths["/api/forge/context/user/settings/{namespace}"]["get"];
type CmmdUserSettingsPutOperation =
  CmmdForgeContextPaths["/api/forge/context/user/settings/{namespace}"]["put"];

export type CmmdForgeAccountPreferences =
  CmmdForgeAccountPreferencesGetOperation["responses"][200]["content"]["application/json"]["data"];
export type UpdateCmmdForgeAccountPreferencesParams = NonNullable<
  CmmdForgeAccountPreferencesPatchOperation["requestBody"]
>["content"]["application/json"];

/**
 * One versioned settings document stored on CMMD for the signed-in user
 * (forge#3515). `namespace` selects the document kind ("ui-settings" or
 * "keybindings"); `revision` is the optimistic-concurrency counter a writer
 * echoes back to make its next write.
 */
export type CmmdUserSettingsDocument =
  CmmdUserSettingsGetOperation["responses"][200]["content"]["application/json"]["data"];
export type PutCmmdUserSettingsParams = NonNullable<
  CmmdUserSettingsPutOperation["requestBody"]
>["content"]["application/json"];
export type CmmdUserSettingsNamespace = CmmdUserSettingsDocument["namespace"];
export type AttestCmmdCodexAccountIdentityParams = NonNullable<
  CmmdCodexAccountIdentityPostOperation["requestBody"]
>["content"]["application/json"];
export type CmmdCodexAccountIdentity =
  CmmdCodexAccountIdentityPostOperation["responses"][200]["content"]["application/json"]["data"];

/** CMMD-owned org provider policy projected by Forge Settings / picker. */
export type CmmdOrgProviderPolicy =
  CmmdOrgProviderPolicyGetOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdOrgProviderPolicyWallet = CmmdOrgProviderPolicy["allowedWallets"][number];
/** CMMD-owned non-secret personal Codex connection and reconnect state. */
export type CmmdCodexCredentialState =
  CmmdCodexCredentialGetOperation["responses"][200]["content"]["application/json"]["data"];
/** CMMD-owned non-secret personal Claude connection and credential generation state. */
export type CmmdClaudeCredentialState =
  CmmdClaudeCredentialGetOperation["responses"][200]["content"]["application/json"]["data"];
type CmmdProviderAccountInventoryData =
  CmmdForgeContextPaths["/api/forge/context/providers/accounts"]["get"]["responses"][200]["content"]["application/json"]["data"];
type CmmdProviderAccountInventoryRow = CmmdProviderAccountInventoryData["accounts"][number];
export type CmmdProviderAccountInventoryAccount = Readonly<
  Omit<CmmdProviderAccountInventoryRow, "legacyProviderAccountIds">
> & {
  readonly legacyProviderAccountIds: Readonly<
    CmmdProviderAccountInventoryRow["legacyProviderAccountIds"]
  >;
};
export interface CmmdProviderAccountInventory {
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
}
export type CmmdProviderUsageSnapshots =
  CmmdProviderUsageGetOperation["responses"][200]["content"]["application/json"]["data"];
/**
 * Last-known 5h/7d usage for one connected provider account. Explicit refresh
 * polls Codex or Claude through CMMD. Claude also records passive turn telemetry,
 * so the stored snapshot can update without opening the Usage page.
 */
export type CmmdProviderUsageSnapshot = CmmdProviderUsageSnapshots["snapshots"][number];
export type CmmdProviderUsageWindow = NonNullable<CmmdProviderUsageSnapshot["fiveHour"]>;
/** One per-model weekly rate-limit window (Claude Code's "Weekly · Fable" row). */
export type CmmdProviderUsageModelWindow = CmmdProviderUsageSnapshot["modelWindows"][number];
/** CMMD-owned exact-generation native Claude readiness for one Forge runtime. */
export type CmmdClaudeRuntimeReadiness =
  CmmdClaudeRuntimeReadinessGetOperation["responses"][200]["content"]["application/json"]["data"];
/** Non-secret identity receipt proving which provider authority reached one exact runtime epoch. */
export type CmmdProviderRuntimeDeliveryReceipt = NonNullable<
  CmmdClaudeRuntimeReadiness["deliveryReceipt"]
>;

export type CmmdSavedDataView =
  CmmdSavedDataViewsGetOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdSavedDataViewDefinition = CmmdSavedDataView["definition"];
export type CmmdSavedDataViewSurface = CmmdSavedDataView["surface"];
export type ListCmmdSavedDataViewsParams = CmmdSavedDataViewsGetOperation["parameters"]["query"];
export type CreateCmmdSavedDataViewParams = NonNullable<
  CmmdSavedDataViewsPostOperation["requestBody"]
>["content"]["application/json"];
export type ImportCmmdSavedDataViewParams = NonNullable<
  CmmdSavedDataViewsImportOperation["requestBody"]
>["content"]["application/json"];
export type ImportCmmdSavedDataViewResult =
  CmmdSavedDataViewsImportOperation["responses"][200]["content"]["application/json"]["data"];
export type UpdateCmmdSavedDataViewParams = NonNullable<
  CmmdSavedDataViewsPatchOperation["requestBody"]
>["content"]["application/json"];
export type DeleteCmmdSavedDataViewParams =
  CmmdSavedDataViewsDeleteOperation["parameters"]["query"];

export type CmmdForgeAutomationPreset =
  CmmdForgeAutomationPresetsOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdForgeAutomationRule =
  CmmdForgeAutomationRulesOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdUserForgeAutomationRule =
  CmmdUserForgeAutomationRulesOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdForgeAutomationRun =
  CmmdForgeAutomationRunsOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdUserForgeAutomationRun =
  CmmdUserForgeAutomationRunsOperation["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CmmdForgeAutomationRunDetail =
  CmmdForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdStartForgeAutomationRunResult =
  CmmdStartForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdRetryForgeAutomationRunResult =
  CmmdRetryForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdCancelForgeAutomationRunResult =
  CmmdCancelForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdApproveForgeAutomationEscalationResult =
  CmmdApproveForgeAutomationEscalationOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdRejectForgeAutomationEscalationResult =
  CmmdRejectForgeAutomationEscalationOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdForgeAutomationRunSnapshot =
  CmmdRuntimeForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdForgeAutomationTerminalSnapshot =
  CmmdRuntimeTerminalSnapshotOperation["responses"][200]["content"]["application/json"]["data"];

export type CmmdForgeAutomationTriggerType = CmmdForgeAutomationPreset["triggerType"];
export type CmmdForgeAutomationStatus = CmmdForgeAutomationRule["status"];
export type CmmdForgeAutomationRunStatus = CmmdForgeAutomationRun["run"]["status"];
export type CmmdForgeAutomationRunStage = CmmdForgeAutomationRun["run"]["currentStage"];
export type CmmdForgeAutomationMode = CmmdForgeAutomationRun["run"]["autonomyMode"];

export type CmmdCreateForgeAutomationRuleParams = NonNullable<
  CmmdCreateForgeAutomationRuleOperation["requestBody"]
>["content"]["application/json"];
export type CmmdUpdateForgeAutomationRuleParams = NonNullable<
  CmmdUpdateForgeAutomationRuleOperation["requestBody"]
>["content"]["application/json"];
export type CmmdCreateUserForgeAutomationRuleParams = NonNullable<
  CmmdUserForgeAutomationRulesOperation["post"]["requestBody"]
>["content"]["application/json"];
export type CmmdUpdateUserForgeAutomationRuleParams = NonNullable<
  CmmdUserForgeAutomationRuleOperation["requestBody"]
>["content"]["application/json"];
export type CmmdStartUserForgeAutomationRunParams = NonNullable<
  CmmdUserForgeAutomationRunsOperation["post"]["requestBody"]
>["content"]["application/json"];
export type CmmdStartUserForgeAutomationRunResult =
  CmmdUserForgeAutomationRunsOperation["post"]["responses"][200]["content"]["application/json"]["data"];
export type CmmdStartForgeAutomationRunParams = NonNullable<
  CmmdStartForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRetryForgeAutomationRunParams = NonNullable<
  CmmdRetryForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdCancelForgeAutomationRunParams = NonNullable<
  CmmdCancelForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdApproveForgeAutomationEscalationParams = NonNullable<
  CmmdApproveForgeAutomationEscalationOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRejectForgeAutomationEscalationParams = NonNullable<
  CmmdRejectForgeAutomationEscalationOperation["requestBody"]
>["content"]["application/json"];
export type CmmdClaimForgeAutomationRunParams = NonNullable<
  CmmdClaimForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdClaimForgeAutomationRunResult =
  CmmdClaimForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdReadForgeAutomationRunParams = NonNullable<
  CmmdRuntimeForgeAutomationRunOperation["parameters"]["query"]
>;
export type CmmdHeartbeatForgeAutomationRunParams = NonNullable<
  CmmdHeartbeatForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdReleaseForgeAutomationRunParams = NonNullable<
  CmmdReleaseForgeAutomationRunOperation["requestBody"]
>["content"]["application/json"];
export type CmmdReportForgeAutomationStageParams = NonNullable<
  CmmdReportForgeAutomationStageOperation["requestBody"]
>["content"]["application/json"];
export type CmmdResolveForgeAutomationModelPolicyParams = NonNullable<
  CmmdResolveForgeAutomationModelPolicyOperation["requestBody"]
>["content"]["application/json"];
export type CmmdCreateForgeAutomationChildWorkItemParams = NonNullable<
  CmmdCreateForgeAutomationChildWorkItemOperation["requestBody"]
>["content"]["application/json"];
export type CmmdForgeAutomationModelPolicy = CmmdForgeAutomationRule["modelPolicy"];
export type CmmdForgeAutomationResolvedModelPolicy =
  CmmdResolveForgeAutomationModelPolicyParams["snapshot"];
export type CmmdHeartbeatForgeAutomationRunResult =
  CmmdHeartbeatForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdReleaseForgeAutomationRunResult =
  CmmdReleaseForgeAutomationRunOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdReportForgeAutomationStageResult =
  CmmdReportForgeAutomationStageOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdResolveForgeAutomationModelPolicyResult =
  CmmdResolveForgeAutomationModelPolicyOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdCreateForgeAutomationChildWorkItemResult =
  CmmdCreateForgeAutomationChildWorkItemOperation["responses"][201]["content"]["application/json"]["data"];

// ── Project Lead controller ──────────────────────────────────────────────────

type CmmdProjectLeadPlanOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/plan"]["get"];
type CmmdProjectLeadAuthorityActivationOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/authority/activate"]["post"];
type CmmdProjectLeadReadinessOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/readiness"]["get"];
type CmmdProjectLeadCapabilitiesOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/capabilities"]["get"];
type CmmdProjectLeadSummaryOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/summary"]["get"];
type CmmdProjectLeadDetailOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/detail"]["get"];
type CmmdProjectLeadBriefingOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/briefing"]["get"];
type CmmdProjectLeadApprovalDecisionOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/approvals/{approvalId}/decision"]["post"];
type CmmdProjectLeadEventsOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/events"]["get"];
type CmmdProjectLeadExplainOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/explain"]["get"];
type CmmdProjectLeadPauseOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/pause"]["post"];
type CmmdProjectLeadResumeOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/resume"]["post"];
type CmmdProjectLeadReserveOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/tasks/{taskId}/reservations"]["post"];
type CmmdProjectLeadReleaseOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/reservations/{reservationId}/release"]["post"];
type CmmdProjectLeadReconcileOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/reconcile"]["post"];
type CmmdProjectLeadResolveAttentionOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/attention/{attentionId}/resolve"]["post"];
type CmmdProjectLeadClaimOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/controller/claim"]["post"];
type CmmdProjectLeadRenewClaimOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/controller/claim/renew"]["post"];
type CmmdProjectLeadYieldClaimOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/controller/claim/yield"]["post"];
type CmmdProjectLeadAcknowledgeEventOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/controller/events/{eventId}/acknowledge"]["post"];
type CmmdProjectLeadRecordDecisionOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/project-lead/controller/decisions"]["post"];

export type CmmdProjectLeadPlan =
  CmmdProjectLeadPlanOperation["responses"][200]["content"]["application/json"]["data"];
export type ActivateCmmdProjectLeadAuthorityResult =
  CmmdProjectLeadAuthorityActivationOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdProjectLeadReadiness =
  CmmdProjectLeadReadinessOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectLeadCapabilities =
  CmmdProjectLeadCapabilitiesOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectLeadSummary =
  CmmdProjectLeadSummaryOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectLeadDetail =
  CmmdProjectLeadDetailOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectLeadBriefing =
  CmmdProjectLeadBriefingOperation["responses"][200]["content"]["application/json"]["data"];
export type DecideCmmdProjectLeadApprovalParams = NonNullable<
  CmmdProjectLeadApprovalDecisionOperation["requestBody"]
>["content"]["application/json"];
export type DecideCmmdProjectLeadApprovalResult =
  CmmdProjectLeadApprovalDecisionOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdProjectLeadEvents =
  CmmdProjectLeadEventsOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdProjectLeadEventsParams = NonNullable<
  CmmdProjectLeadEventsOperation["parameters"]["query"]
>;
export type CmmdProjectLeadExplanation =
  CmmdProjectLeadExplainOperation["responses"][200]["content"]["application/json"]["data"];
export type PauseCmmdProjectLeadParams = NonNullable<
  CmmdProjectLeadPauseOperation["requestBody"]
>["content"]["application/json"];
export type PauseCmmdProjectLeadResult =
  CmmdProjectLeadPauseOperation["responses"][201]["content"]["application/json"]["data"];
export type ResumeCmmdProjectLeadParams = NonNullable<
  CmmdProjectLeadResumeOperation["requestBody"]
>["content"]["application/json"];
export type ResumeCmmdProjectLeadResult =
  CmmdProjectLeadResumeOperation["responses"][201]["content"]["application/json"]["data"];
export type ReserveCmmdProjectLeadTaskParams = NonNullable<
  CmmdProjectLeadReserveOperation["requestBody"]
>["content"]["application/json"];
export type ReserveCmmdProjectLeadTaskResult =
  CmmdProjectLeadReserveOperation["responses"][201]["content"]["application/json"]["data"];
export type ReleaseCmmdProjectLeadReservationParams = NonNullable<
  CmmdProjectLeadReleaseOperation["requestBody"]
>["content"]["application/json"];
export type ReleaseCmmdProjectLeadReservationResult =
  CmmdProjectLeadReleaseOperation["responses"][201]["content"]["application/json"]["data"];
export type ReconcileCmmdProjectLeadParams = NonNullable<
  CmmdProjectLeadReconcileOperation["requestBody"]
>["content"]["application/json"];
export type ReconcileCmmdProjectLeadResult =
  CmmdProjectLeadReconcileOperation["responses"][201]["content"]["application/json"]["data"];
export type ResolveCmmdProjectLeadAttentionParams = NonNullable<
  CmmdProjectLeadResolveAttentionOperation["requestBody"]
>["content"]["application/json"];
export type ResolveCmmdProjectLeadAttentionResult =
  CmmdProjectLeadResolveAttentionOperation["responses"][201]["content"]["application/json"]["data"];
export type ClaimCmmdProjectLeadParams = NonNullable<
  CmmdProjectLeadClaimOperation["requestBody"]
>["content"]["application/json"];
export type ClaimCmmdProjectLeadResult =
  CmmdProjectLeadClaimOperation["responses"][201]["content"]["application/json"]["data"];
export type RenewCmmdProjectLeadClaimParams = NonNullable<
  CmmdProjectLeadRenewClaimOperation["requestBody"]
>["content"]["application/json"];
export type RenewCmmdProjectLeadClaimResult =
  CmmdProjectLeadRenewClaimOperation["responses"][200]["content"]["application/json"]["data"];
export type YieldCmmdProjectLeadClaimParams = NonNullable<
  CmmdProjectLeadYieldClaimOperation["requestBody"]
>["content"]["application/json"];
export type YieldCmmdProjectLeadClaimResult =
  CmmdProjectLeadYieldClaimOperation["responses"][200]["content"]["application/json"]["data"];
export type AcknowledgeCmmdProjectLeadEventParams = NonNullable<
  CmmdProjectLeadAcknowledgeEventOperation["requestBody"]
>["content"]["application/json"];
export type AcknowledgeCmmdProjectLeadEventResult =
  CmmdProjectLeadAcknowledgeEventOperation["responses"][200]["content"]["application/json"]["data"];
export type RecordCmmdProjectLeadDecisionParams = NonNullable<
  CmmdProjectLeadRecordDecisionOperation["requestBody"]
>["content"]["application/json"];
export type CmmdProjectLeadDecision = RecordCmmdProjectLeadDecisionParams["decision"];
export type RecordCmmdProjectLeadDecisionResult =
  CmmdProjectLeadRecordDecisionOperation["responses"][201]["content"]["application/json"]["data"];

// ── Forge thread ↔ CMMD task link ─────────────────────────────────────────────

export interface CmmdTaskLink {
  readonly taskId: string;
  readonly taskTitle: string;
  readonly projectId: string | null;
  readonly campaignId: string | null;
}

// ── Context API envelope ─────────────────────────────────────────────────────

/** Universal response envelope for all `/api/forge/context/*` endpoints. */
export interface CmmdEnvelope<T> {
  readonly data: T;
  readonly meta: {
    readonly domain: string;
    readonly timestamp: string;
    readonly version: string;
  };
  readonly pagination?: CmmdPaginationMeta;
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

export interface CmmdBootstrapIdentity {
  readonly id: number;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly avatarUrl: string | null;
  readonly timezone: string | null;
}

export interface CmmdBootstrapWorkspace {
  readonly id: number;
  readonly name: string;
  readonly slug: string;
  readonly type: string | null;
  readonly role: string;
  readonly lastActiveAt: string | null;
  readonly logoUrl?: string | null;
  readonly logo_url?: string | null;
  readonly logo?: string | null;
  readonly iconUrl?: string | null;
  readonly icon_url?: string | null;
  readonly icon?: string | null;
}

export interface CmmdBootstrapData {
  readonly identity: CmmdBootstrapIdentity;
  readonly activeWorkspace: CmmdBootstrapWorkspace | null;
  readonly workspaces: ReadonlyArray<CmmdBootstrapWorkspace>;
  readonly apps: Readonly<Record<string, boolean>>;
  readonly permissions: {
    readonly canManageMembers: boolean;
    readonly canManageApps: boolean;
    /**
     * Owner or admin of the ACTIVE WORKSPACE — true for anyone who owns any
     * workspace. Not a platform capability.
     */
    readonly isAdmin: boolean;
    /**
     * Active membership of the CMMD Platform organization: the people who run
     * the platform, as opposed to the people who own a workspace on it.
     *
     * Gate platform-wide surfaces on this and never on `isAdmin` above.
     * Optional because a CMMD deployment that predates the field simply does
     * not send it, and an absent answer is a negative one.
     */
    readonly isPlatformAdmin?: boolean;
  };
  readonly featureFlags: Readonly<Record<string, boolean>>;
  readonly warnings: ReadonlyArray<string>;
}

// ── Billing ───────────────────────────────────────────────────────────────────

type CmmdBillingOperation = CmmdForgeContextPaths["/api/forge/context/billing"]["get"];
type CmmdBillingSeriesOperation = CmmdForgeContextPaths["/api/forge/context/billing/series"]["get"];

/**
 * The workspace's CMMD plan and how much of each metered allowance is left.
 *
 * This is subscription consumption. It is not the same quantity as the usage
 * summary, which estimates what the same work would have cost at direct
 * provider list prices — see {@link CmmdUsageSummaryInput}.
 */
export type CmmdBillingContext =
  CmmdBillingOperation["responses"][200]["content"]["application/json"]["data"];

export type CmmdBillingMeter = CmmdBillingContext["meters"][string];

/** Per-day consumption across the current period, one entry per charted meter. */
export type CmmdBillingSeries =
  CmmdBillingSeriesOperation["responses"][200]["content"]["application/json"]["data"];

export type CmmdBillingSeriesDay = CmmdBillingSeries["days"][number];

/** The meters the billing chart plots, narrowed from the generated response. */
export type CmmdBillingSeriesMeterKey = CmmdBillingSeries["meterKeys"][number];

// ── Device registry ──────────────────────────────────────────────────────────

export type CmmdDeviceRegistryRuntimeSource = "registered" | "forge-cloud";

export interface CmmdDeviceRegistryHost {
  readonly id: number;
  readonly hostKey: string;
  readonly displayName: string;
  readonly actualDeviceName: string;
  readonly nickname: string | null;
  readonly platform: string;
  readonly lastSeenAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly metadata: Readonly<Record<string, unknown>> | null;
}

export interface CmmdDeviceRegistryDevice {
  readonly id: number;
  readonly hostId?: number | null;
  readonly hostKey?: string | null;
  readonly deviceKey: string;
  readonly displayName: string;
  readonly actualDeviceName: string | null;
  readonly nickname: string | null;
  readonly platform: string;
  readonly deviceType: string;
  readonly trustState: string;
  readonly lastSeenAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly metadata: Readonly<Record<string, unknown>> | null;
  readonly authorization: {
    readonly bound: boolean;
    readonly revoked: boolean;
  };
}

export interface CmmdDeviceRegistryRuntime {
  readonly id: string;
  readonly source: CmmdDeviceRegistryRuntimeSource;
  readonly runtimeKind: "desktop-local" | "cloud-vm" | "unknown";
  readonly displayName: string;
  readonly status: string;
  readonly endpointUrl: string | null;
  readonly hostId?: number | null;
  readonly deviceId: number | null;
  readonly projectId: number | null;
  readonly projectName: string | null;
  readonly workspaceRoot: string | null;
  readonly githubRepoFullName: string | null;
  readonly lastSeenAt: string | null;
  /** Optional only for cached responses written before the CMMD reachability contract shipped. */
  readonly reachability?: {
    readonly status: "online" | "suspect" | "offline";
    readonly lastReachableAt: string | null;
    readonly source: "relay-lease" | "legacy-registration" | "cloud-runtime";
  };
  readonly metadata: Readonly<Record<string, unknown>> | null;
}

export interface CmmdDeviceRegistrySnapshot {
  readonly hosts?: ReadonlyArray<CmmdDeviceRegistryHost>;
  readonly devices: ReadonlyArray<CmmdDeviceRegistryDevice>;
  readonly runtimes: ReadonlyArray<CmmdDeviceRegistryRuntime>;
}

type CmmdRuntimeInventoryGetOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-inventory"]["get"];
export type CmmdRuntimeInventorySnapshot =
  CmmdRuntimeInventoryGetOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdRuntimeInventoryRuntime = CmmdRuntimeInventorySnapshot["runtimes"][number];
export type CmmdRuntimeInventoryProjectSummary = CmmdRuntimeInventoryRuntime["projects"][number];
export type CmmdRuntimeInventoryThreadSummary =
  CmmdRuntimeInventoryProjectSummary["threads"][number];
/** Archive projection embedded in the runtime inventory thread summary. */
export type CmmdThreadArchiveSummary = NonNullable<CmmdRuntimeInventoryThreadSummary["archive"]>;
export type CmmdRuntimeInventoryStatus = CmmdRuntimeInventoryRuntime["inventoryStatus"];

type CmmdThreadDispositionPutOperation =
  CmmdForgeContextPaths["/api/forge/context/threads/{runtimeId}/{projectId}/{threadId}/disposition"]["put"];
/** CMMD-owned filing state shared by every Forge surface. */
export type CmmdThreadDisposition =
  CmmdThreadDispositionPutOperation["responses"][200]["content"]["application/json"]["data"];
export type PutCmmdThreadDispositionParams = NonNullable<
  CmmdThreadDispositionPutOperation["requestBody"]
>["content"]["application/json"];
export type CmmdThreadDispositionAction = PutCmmdThreadDispositionParams["action"];

type CmmdThreadArchiveOwnerOperation =
  CmmdForgeContextPaths["/api/forge/context/threads/{runtimeId}/{projectId}/{threadId}/archive"]["post"];
/** CMMD-owned archive workflow state shared by every Forge control surface. */
export type CmmdThreadArchive =
  CmmdThreadArchiveOwnerOperation["responses"][200]["content"]["application/json"]["data"];
export type PostCmmdThreadArchiveParams = NonNullable<
  CmmdThreadArchiveOwnerOperation["requestBody"]
>["content"]["application/json"];

type CmmdRuntimeThreadArchiveListOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/thread-archives"]["get"];
type CmmdRuntimeThreadArchiveClaimOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/thread-archives/{operationId}/claim"]["post"];
type CmmdRuntimeThreadArchiveCompleteOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/thread-archives/{operationId}/complete"]["post"];
type CmmdRuntimeThreadArchiveFailOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/thread-archives/{operationId}/fail"]["post"];

export type CmmdRuntimeThreadArchiveList =
  CmmdRuntimeThreadArchiveListOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdRuntimeThreadArchiveOperation = CmmdRuntimeThreadArchiveList["operations"][number];
export type CompleteCmmdRuntimeThreadArchiveParams = NonNullable<
  CmmdRuntimeThreadArchiveCompleteOperation["requestBody"]
>["content"]["application/json"];
export type FailCmmdRuntimeThreadArchiveParams = NonNullable<
  CmmdRuntimeThreadArchiveFailOperation["requestBody"]
>["content"]["application/json"];

type CmmdRuntimeThreadArchiveGrantListOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/thread-archives"]["get"];
type CmmdRuntimeThreadArchiveGrantClaimOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/thread-archives/{operationId}/claim"]["post"];
type CmmdRuntimeThreadArchiveGrantCompleteOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/thread-archives/{operationId}/complete"]["post"];
type CmmdRuntimeThreadArchiveGrantFailOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/thread-archives/{operationId}/fail"]["post"];

type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _threadArchiveListLanesAgree: AssertEqual<
  CmmdRuntimeThreadArchiveList,
  CmmdRuntimeThreadArchiveGrantListOperation["responses"][200]["content"]["application/json"]["data"]
> = true;
const _threadArchiveClaimLanesAgree: AssertEqual<
  CmmdRuntimeThreadArchiveClaimOperation["responses"][200]["content"]["application/json"]["data"],
  CmmdRuntimeThreadArchiveGrantClaimOperation["responses"][200]["content"]["application/json"]["data"]
> = true;
const _threadArchiveCompleteLanesAgree: AssertEqual<
  CmmdRuntimeThreadArchiveCompleteOperation["responses"][200]["content"]["application/json"]["data"],
  CmmdRuntimeThreadArchiveGrantCompleteOperation["responses"][200]["content"]["application/json"]["data"]
> = true;
const _threadArchiveFailLanesAgree: AssertEqual<
  CmmdRuntimeThreadArchiveFailOperation["responses"][200]["content"]["application/json"]["data"],
  CmmdRuntimeThreadArchiveGrantFailOperation["responses"][200]["content"]["application/json"]["data"]
> = true;
void _threadArchiveListLanesAgree;
void _threadArchiveClaimLanesAgree;
void _threadArchiveCompleteLanesAgree;
void _threadArchiveFailLanesAgree;

type CmmdRuntimeDiagnosticsGetOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-diagnostics/v2/runtimes/{runtimeId}"]["get"];
/**
 * The canonical bounded CMMD diagnostics observation. It deliberately contains
 * no runtime endpoint, process, log, credential, or arbitrary metadata field,
 * and reading it never wakes the target.
 */
export type CmmdRuntimeDiagnosticsSnapshot =
  CmmdRuntimeDiagnosticsGetOperation["responses"][200]["content"]["application/json"]["data"];
export type ForgeRuntimePreparedContract = CmmdRuntimeDiagnosticsSnapshot["preparedContract"];

type CmmdCloudProjectLifecyclePreflightGetOperation =
  CmmdForgeContextPaths["/api/forge/context/cloud-projects/{projectId}/lifecycle-preflight"]["get"];
export type CmmdCloudProjectLifecyclePreflightResponse =
  CmmdCloudProjectLifecyclePreflightGetOperation["responses"][200]["content"]["application/json"]["data"];
/**
 * A bounded owner-scoped lifecycle receipt read under CMMD's project lifecycle
 * lock. It contains persisted evidence only and never implies a live Sprite
 * probe or permission to run a lifecycle command.
 */
export type CmmdCloudProjectLifecyclePreflight =
  CmmdCloudProjectLifecyclePreflightResponse["preflight"];

// Card 1359 — ephemeral personal-provider sign-in runtime (cloud-only users).
type CmmdAuthRuntimeGetOperation = CmmdForgeContextPaths["/api/forge/context/auth-runtime"]["get"];
export type CmmdAuthRuntimeState =
  CmmdAuthRuntimeGetOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdAuthRuntime = NonNullable<CmmdAuthRuntimeState["runtime"]>;
type CmmdAuthRuntimeConnectOperation =
  CmmdForgeContextPaths["/api/forge/context/auth-runtime/connect"]["post"];
export type CmmdAuthRuntimeConnectDescriptor =
  CmmdAuthRuntimeConnectOperation["responses"][200]["content"]["application/json"]["data"];

type CmmdRuntimeThreadGetOperation =
  CmmdForgeContextPaths["/api/forge/context/threads/{runtimeId}/{threadId}"]["get"];
type CmmdRuntimeInventoryReplaceOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/inventory"]["put"];
type CmmdRuntimeThreadAppendOperation =
  CmmdForgeContextPaths["/api/forge/context/runtime-replication/runtimes/{runtimeId}/threads/{threadId}/events"]["post"];

export type CmmdRuntimeThreadReplica =
  CmmdRuntimeThreadGetOperation["responses"][200]["content"]["application/json"]["data"];
export type ReplaceCmmdRuntimeInventoryParams = NonNullable<
  CmmdRuntimeInventoryReplaceOperation["requestBody"]
>["content"]["application/json"];
export type AppendCmmdRuntimeThreadEventsParams = NonNullable<
  CmmdRuntimeThreadAppendOperation["requestBody"]
>["content"]["application/json"];
export type AppendCmmdRuntimeThreadEventsResult =
  CmmdRuntimeThreadAppendOperation["responses"][200]["content"]["application/json"]["data"];

type CmmdDeviceRegistrationOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/register"]["post"];
export type RegisterCmmdDeviceParams = NonNullable<
  CmmdDeviceRegistrationOperation["requestBody"]
>["content"]["application/json"];
export type CmmdDeviceRegistrationResult =
  CmmdDeviceRegistrationOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdDeviceRegistrationReceipt = CmmdDeviceRegistrationResult["registrationReceipt"];

type CmmdDeviceKeyRegistrationOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/{deviceId}/keys"]["post"];
type CmmdDeviceKeyRotationChallengeOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/{deviceId}/key-rotation-challenges"]["post"];
type CmmdDeviceKeyRotationOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/{deviceId}/keys/rotate"]["post"];
type CmmdRelayChallengeOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/relay-challenges"]["post"];
type CmmdRelayHostTicketOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/relay-host-tickets"]["post"];
type CmmdRelayControlRouteOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/relay-routes"]["post"];
type CmmdRelayActionGrantOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/relay-action-grants"]["post"];
type CmmdRuntimeAccessGrantOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/access-grants"]["post"];
type CmmdRuntimeAccessRevokeOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/runtimes/{runtimeId}/access-grants/{grantId}/revoke"]["post"];
type CmmdRelayVerificationKeysOperation =
  CmmdForgeContextPaths["/api/forge/context/devices/relay-verification-keys"]["get"];

export type RegisterCmmdDeviceKeyParams = NonNullable<
  CmmdDeviceKeyRegistrationOperation["requestBody"]
>["content"]["application/json"];
export type CmmdDeviceKeyRegistration =
  CmmdDeviceKeyRegistrationOperation["responses"][201]["content"]["application/json"]["data"];
export type CreateCmmdDeviceKeyRotationChallengeParams = NonNullable<
  CmmdDeviceKeyRotationChallengeOperation["requestBody"]
>["content"]["application/json"];
export type CmmdDeviceKeyRotationChallenge =
  CmmdDeviceKeyRotationChallengeOperation["responses"][201]["content"]["application/json"]["data"];
export type ExchangeCmmdDeviceKeyRotationParams = NonNullable<
  CmmdDeviceKeyRotationOperation["requestBody"]
>["content"]["application/json"];
export type CreateCmmdRelayChallengeParams = NonNullable<
  CmmdRelayChallengeOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRelayChallenge =
  CmmdRelayChallengeOperation["responses"][201]["content"]["application/json"]["data"];
export type ExchangeCmmdRelayHostTicketParams = NonNullable<
  CmmdRelayHostTicketOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRelayHostTicket =
  CmmdRelayHostTicketOperation["responses"][201]["content"]["application/json"]["data"];
export type CreateCmmdRelayControlRouteParams = NonNullable<
  CmmdRelayControlRouteOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRelayControlRoute =
  CmmdRelayControlRouteOperation["responses"][201]["content"]["application/json"]["data"];
export type CreateCmmdRelayActionGrantParams = NonNullable<
  CmmdRelayActionGrantOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRelayActionGrant =
  CmmdRelayActionGrantOperation["responses"][201]["content"]["application/json"]["data"];
export type GrantCmmdRuntimeAccessParams = NonNullable<
  CmmdRuntimeAccessGrantOperation["requestBody"]
>["content"]["application/json"];
export type CmmdRuntimeAccessGrant =
  CmmdRuntimeAccessGrantOperation["responses"][201]["content"]["application/json"]["data"];
export type CmmdRuntimeAccessRevokeResult =
  CmmdRuntimeAccessRevokeOperation["responses"][200]["content"]["application/json"]["data"];
export type CmmdRelayVerificationKeySet =
  CmmdRelayVerificationKeysOperation["responses"][200]["content"]["application/json"]["data"];

// ── Project context (updates, milestones, team) ───────────────────────────────

export interface CmmdProjectUpdate {
  readonly id: number;
  readonly projectId: number;
  readonly organizationId: number;
  readonly authorId: number;
  readonly status: "on_track" | "at_risk" | "off_track" | "complete";
  readonly body: string;
  readonly createdAt: string;
}

type CmmdProjectMilestonesOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/milestones"]["get"];
type CmmdCreateProjectMilestoneOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/milestones"]["post"];
type CmmdUpdateProjectMilestoneOperation =
  CmmdForgeContextPaths["/api/forge/context/projects/{projectId}/milestones/{milestoneId}"]["patch"];
type CmmdForgeTaskDetailOperation =
  CmmdForgeContextPaths["/api/forge/context/tasks/{taskId}"]["get"];

export type CmmdProjectMilestone =
  CmmdProjectMilestonesOperation["responses"][200]["content"]["application/json"]["data"][number];
export type CreateCmmdProjectMilestoneParams = NonNullable<
  CmmdCreateProjectMilestoneOperation["requestBody"]
>["content"]["application/json"];
export type UpdateCmmdProjectMilestoneParams = NonNullable<
  CmmdUpdateProjectMilestoneOperation["requestBody"]
>["content"]["application/json"];
export type CmmdForgeTaskDetail =
  CmmdForgeTaskDetailOperation["responses"][200]["content"]["application/json"]["data"];

export interface CmmdProjectTeamMember {
  readonly id: number;
  readonly userId: number;
  readonly projectId: number;
  readonly role: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly avatarUrl: string | null;
}

// ── Campaign context (updates, health) ────────────────────────────────────────

export interface CmmdCampaignUpdate {
  readonly id: number;
  readonly campaignId: number;
  readonly organizationId: number;
  readonly authorId: number;
  readonly status: "on_track" | "at_risk" | "off_track" | "complete";
  readonly body: string;
  readonly createdAt: string;
}

export interface CmmdCampaignHealth {
  readonly campaignId: number;
  readonly tasksTotal: number;
  readonly tasksDone: number;
  readonly projectsTotal: number;
  readonly projectsDone: number;
  readonly updatedAt: string | null;
}

// ── Brain collections ─────────────────────────────────────────────────────────

export interface CmmdBrainCollection {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly parentCollectionId: number | null;
  readonly color: string | null;
  readonly icon: string | null;
  readonly scope: "account" | "workspace" | null;
  readonly organizationId: number;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

export interface CmmdCreateBrainCollectionInput {
  readonly name: string;
  readonly description?: string;
  readonly parentCollectionId?: number;
  readonly color?: string;
  readonly icon?: string;
  readonly scope?: "account" | "workspace";
}

export interface CmmdUpdateBrainCollectionInput {
  readonly name?: string;
  readonly description?: string;
  readonly color?: string;
  readonly icon?: string;
}

// ── Brain documents ───────────────────────────────────────────────────────────

export interface CmmdBrainDocument {
  readonly id: number;
  readonly title: string;
  readonly content: string | null;
  readonly tags: ReadonlyArray<string>;
  readonly collectionId: number | null;
  readonly organizationId: number;
  readonly sourceSystem: string | null;
  readonly isPublic: boolean;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

export interface CmmdCreateBrainDocumentInput {
  readonly title: string;
  readonly content?: string;
  readonly collectionId?: number | null;
  readonly tags?: ReadonlyArray<string>;
}

export interface CmmdUpdateBrainDocumentInput {
  readonly title?: string;
  readonly content?: string;
  readonly collectionId?: number | null;
  readonly tags?: ReadonlyArray<string>;
}

// ── Strategy ──────────────────────────────────────────────────────────────────

export interface CmmdVision {
  readonly id: number;
  readonly organizationId: number;
  readonly teamId: number | null;
  readonly purpose: string | null;
  readonly vision: string | null;
  readonly victory: string | null;
  readonly victoryDetails: string | null;
  readonly victoryTargetDate: string | null;
  readonly updatedAt: string | null;
}

export interface CmmdUpsertVisionInput {
  readonly purpose?: string;
  readonly vision?: string;
  readonly victory?: string;
  readonly victoryDetails?: string;
  readonly victoryTargetDate?: string;
  readonly teamId?: number;
}

export interface CmmdCoreValue {
  readonly id: number;
  readonly organizationId: number;
  readonly teamId: number | null;
  readonly name: string;
  readonly description: string | null;
  readonly willNotTolerate: string | null;
  readonly sortOrder: number;
  readonly createdAt: string;
}

export interface CmmdCreateCoreValueInput {
  readonly name: string;
  readonly description?: string;
  readonly willNotTolerate?: string;
  readonly sortOrder?: number;
  readonly teamId?: number;
}

export interface CmmdHorizonGoal {
  readonly id: number;
  readonly organizationId: number;
  readonly teamId: number | null;
  readonly title: string;
  readonly description: string | null;
  readonly targetDate: string | null;
  readonly status: string | null;
}

export interface CmmdCreateHorizonGoalInput {
  readonly title: string;
  readonly description?: string;
  readonly targetDate?: string;
  readonly teamId?: number;
  readonly status?: string;
}

export interface CmmdStrategySnapshot {
  readonly vision: CmmdVision | null;
  readonly coreValues: ReadonlyArray<CmmdCoreValue>;
  readonly horizonGoals: ReadonlyArray<CmmdHorizonGoal>;
}

// ── Goals ─────────────────────────────────────────────────────────────────────

export interface CmmdGoal {
  readonly id: number;
  readonly organizationId: number;
  readonly teamId: number | null;
  readonly title: string;
  readonly description: string | null;
  readonly ownerId: number;
  readonly ownerName: string | null;
  readonly category: string;
  readonly timeframe: string;
  readonly status: string | null;
  readonly progress: number;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

export interface CmmdCreateGoalInput {
  readonly title: string;
  readonly description?: string;
  readonly ownerId: number;
  readonly category: string;
  readonly timeframe: string;
  readonly status?: string;
  readonly progress?: number;
}

export interface CmmdUpdateGoalInput {
  readonly title?: string;
  readonly description?: string;
  readonly ownerId?: number;
  readonly category?: string;
  readonly timeframe?: string;
  readonly status?: string;
  readonly progress?: number;
}

// ── Plans ─────────────────────────────────────────────────────────────────────

export interface CmmdPlanStep {
  readonly id: number;
  readonly planId: number;
  readonly organizationId: number;
  readonly title: string;
  readonly description: string | null;
  readonly depth: number;
  readonly order: number;
  readonly outputType: string;
  readonly executionTarget: string;
  readonly status: string | null;
  readonly result: string | null;
}

export interface CmmdPlan {
  readonly id: number;
  readonly organizationId: number;
  readonly userId: number;
  readonly title: string;
  readonly rootGoal: string;
  readonly status: string;
  readonly steps?: ReadonlyArray<CmmdPlanStep>;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

export interface CmmdCreatePlanInput {
  readonly title: string;
  readonly rootGoal: string;
  readonly steps?: ReadonlyArray<{
    readonly title: string;
    readonly description?: string;
    readonly outputType?: string;
    readonly executionTarget?: string;
  }>;
}

export interface CmmdUpdatePlanInput {
  readonly title?: string;
  readonly rootGoal?: string;
  readonly status?: string;
}

// ── Chats / Threads ───────────────────────────────────────────────────────────

export interface CmmdChat {
  readonly id: number;
  readonly userId: number;
  readonly organizationId: number;
  readonly title: string | null;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

// ── Comms ─────────────────────────────────────────────────────────────────────

export interface CmmdCommsChannel {
  readonly id: number;
  readonly organizationId: number;
  readonly name: string;
  readonly description: string | null;
  readonly isPrivate: boolean;
  readonly createdAt: string;
}

export interface CmmdCommsMessage {
  readonly id: number;
  readonly channelId: number;
  readonly senderId: number;
  readonly content: string;
  readonly isEdited: boolean;
  readonly deletedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

// ── Calendar ──────────────────────────────────────────────────────────────────

export interface CmmdCalendar {
  readonly id: number;
  readonly userId: number;
  readonly organizationId: number;
  readonly name: string;
  readonly color: string | null;
  readonly isDefault: boolean;
}

export interface CmmdCalendarEvent {
  readonly id: number;
  readonly calendarId: number;
  readonly organizationId: number;
  readonly title: string;
  readonly description: string | null;
  readonly startAt: string;
  readonly endAt: string;
  readonly isAllDay: boolean;
  readonly location: string | null;
  readonly createdAt: string;
  readonly updatedAt: string | null;
}

export interface CmmdCreateCalendarEventInput {
  readonly calendarId: number;
  readonly title: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly description?: string;
  readonly isAllDay?: boolean;
  readonly location?: string;
}

export interface CmmdUpdateCalendarEventInput {
  readonly title?: string;
  readonly startAt?: string;
  readonly endAt?: string;
  readonly description?: string | null;
  readonly isAllDay?: boolean;
  readonly location?: string | null;
}

// ── People / Teams ────────────────────────────────────────────────────────────

export interface CmmdOrgMember {
  readonly id: number;
  readonly organizationId: number;
  readonly userId: number;
  readonly role: string | null;
  readonly user: {
    readonly id: number;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly email: string;
    readonly username: string | null;
    readonly avatarUrl: string | null;
  };
}

export interface CmmdTeam {
  readonly id: number;
  readonly organizationId: number;
  readonly name: string;
  readonly description: string | null;
  readonly createdAt: string;
}
